import { type User } from '@kalos/kalos-rpc';
import { Avatar, AvatarFallback, AvatarImage, cn } from '@kalos/ui';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { getEmployeeImageQueryConfig } from '../../../../../hooks/react-query/useEmployeesQuery';

export const EmployeeAvatar = ({
  image,
  fallback,
  className,
}: {
  image: User['image'];
  fallback: React.ReactNode;
  className?: string;
}) => {
  const hasImageKey = Boolean(image?.trim());
  const [imgDecoded, setImgDecoded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  const imageQuery = useQuery({
    ...getEmployeeImageQueryConfig({ imageUrl: image || '' }),
    enabled: hasImageKey,
  });

  const resolvedUrl =
    hasImageKey &&
    imageQuery.isSuccess &&
    typeof imageQuery.data === 'string' &&
    imageQuery.data.trim().length > 0
      ? imageQuery.data.trim()
      : undefined;

  useEffect(() => {
    setImgDecoded(false);
    setImgFailed(false);
  }, [resolvedUrl]);

  const queryResolvingUrl =
    hasImageKey &&
    !imageQuery.isError &&
    resolvedUrl === undefined &&
    (imageQuery.isPending || imageQuery.isFetching);

  const browserLoadingImage = Boolean(resolvedUrl) && !imgDecoded && !imgFailed;

  const showLoader =
    hasImageKey && !imageQuery.isError && (queryResolvingUrl || browserLoadingImage);

  return (
    <div className="relative inline-flex">
      <Avatar className={className}>
        {resolvedUrl ? (
          <AvatarImage
            className="object-cover"
            src={resolvedUrl}
            onLoad={() => setImgDecoded(true)}
            onError={() => {
              setImgFailed(true);
              setImgDecoded(true);
            }}
          />
        ) : null}
        <AvatarFallback className={cn(showLoader && 'opacity-0')}>{fallback}</AvatarFallback>
      </Avatar>
      {showLoader ? (
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-white/90"
          role="status"
          aria-label="Loading profile photo"
        >
          <Loader2 className="text-primary h-6 w-6 shrink-0 animate-spin" aria-hidden />
        </div>
      ) : null}
    </div>
  );
};
